import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import React, { memo, useCallback, useMemo } from 'react';
import {
  AreaData,
  DateData,
  DurationData,
  LocationChangeParameters,
  LocationData,
  PersonsData,
  RoomsState,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import SearchBarDateDropdown from '@/components/organisms/search-bar/shared/search-bar-date-dropdown/search-bar-date-dropdown';
import SearchBarDurationDropdown from '@/components/organisms/search-bar/shared/search-bar-duration-dropdown/search-bar-duration-dropdown';
import SearchBarLocationsDropdown from '@/components/organisms/search-bar/shared/search-bar-locations-dropdown/search-bar-locations-dropdown';
import SearchBarPersonsDropdown from '@/components/organisms/search-bar/shared/search-bar-persons-dropdown/search-bar-persons-dropdown';
import SearchBarWidgetDropdown from '../search-bar-widget-dropdown/search-bar-widget-dropdown';
import styles from './search-bar-dropdown.module.scss';

export interface SearchBarSectionData {
  disabled?: boolean;
  dropdownData?:
    | AreaData
    | DateData
    | DurationData
    | LocationData
    | PersonsData;
  id: number;
  isActive?: boolean;
  isNotified?: boolean;
  label: string;
  placeholder?: string;
  type: SearchBarWrapperSectionType;
  value?: string[] | string;
}

export interface SearchBarDropdownProps {
  /** The active section id */
  activeSection?: number;
  /** The class name to apply */
  className?: string;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Indicates whether the date section is loading */
  isLoadingDate?: boolean;
  /** Indicates whether the locations section is loading */
  isLoadingLocations?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** An event that is triggered on date change */
  onChangeDate?: (dates: Date[]) => void;
  /** An event that is triggered on duration change */
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on location change */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on persons change */
  onChangePersons?: (roomsState: RoomsState) => void;
  /** An event that is triggered on transportation change */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on close click */
  onClickClose?: () => void;
  /** An event that is triggered on next click */
  onClickNext?: () => void;
  /** An event that is triggered on search click */
  onClickSearch?: () => void;
  /** The search settings */
  searchSettings?: Content.Components['SearchSettingsDto'];
  /** Sections to render in the search bar */
  sections?: SearchBarSectionData[];
}

const SearchBarDropdown = memo(
  ({
    activeSection = -1,
    className,
    customRangeStyle = false,
    isLoadingDate = false,
    isLoadingLocations = false,
    isWidget = false,
    onChangeDate,
    onChangeDuration,
    onChangeLocations,
    onChangePersons,
    onChangeTransportation,
    onClickBack,
    onClickClose,
    onClickNext,
    onClickSearch,
    searchSettings,
    sections = [],
    ...rest
  }: SearchBarDropdownProps) => {
    const { dateSection, durationSection, locationSection, widgetSection } =
      useMemo(() => {
        const date = sections.find(
          section => section.type === SearchBarWrapperSectionType.DATE,
        );
        const duration = sections.find(
          section => section.type === SearchBarWrapperSectionType.DURATION,
        );
        const location = sections.find(
          section => section.type === SearchBarWrapperSectionType.LOCATIONS,
        );
        const widget = sections.find(section => section.id === activeSection);

        return {
          dateSection: date,
          durationSection: duration,
          locationSection: location,
          widgetSection: widget,
        };
      }, [sections, activeSection]);

    const activeSectionType = widgetSection?.type;
    const activeDateSection =
      dateSection?.type === activeSectionType ? dateSection : undefined;
    const activeLocationSection =
      locationSection?.type === activeSectionType ? locationSection : undefined;

    const renderSearchBarSectionDropdown = useCallback(
      (section: SearchBarSectionData) => {
        switch (section.type) {
          case SearchBarWrapperSectionType.DURATION:
            return (
              <SearchBarDurationDropdown
                className={classNames(
                  styles.sectionDropdown,
                  styles.isDurationDropdown,
                )}
                description={section.value ?? section.placeholder}
                isVisible={section.id === activeSection}
                label={section.label}
                onChange={onChangeDuration}
                onClickClose={onClickClose}
                onClickNext={onClickNext}
                selectOptions={
                  (section.dropdownData as DurationData)?.durationOptions ?? []
                }
              />
            );

          case SearchBarWrapperSectionType.PERSONS:
            return (
              <SearchBarPersonsDropdown
                adultThreshold={
                  (section.dropdownData as PersonsData)?.adultThreshold ?? 0
                }
                className={classNames(
                  styles.sectionDropdown,
                  styles.isPersonsDropdown,
                )}
                defaultAdultNumber={
                  (section.dropdownData as PersonsData)?.defaultAdultNumber
                }
                description={section.value ?? section.placeholder}
                isNotified={section.isNotified}
                isVisible={section.id === activeSection}
                label={section.label}
                maxPersons={(section.dropdownData as PersonsData)?.maxPersons}
                maxRooms={(section.dropdownData as PersonsData)?.maxRooms}
                onChange={onChangePersons}
                onClickBack={onClickBack}
                onClickClose={onClickClose}
                onClickNext={onClickNext}
                roomDistributionWarning={
                  (section.dropdownData as PersonsData)?.roomDistributionWarning
                }
                roomsState={(section.dropdownData as PersonsData)?.roomsState}
              />
            );

          default:
            return null;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [activeSection],
    );

    return (
      <div
        {...rest}
        className={classNames(styles.component, className, {
          [styles.isSearchWidget]: isWidget,
        })}
      >
        {!isWidget ? (
          <React.Fragment>
            <div className={classNames(styles.searchBarWrapper)}>
              {sections.map(section => (
                <div
                  key={section.id}
                  className={classNames(styles.searchSectionWrapper)}
                >
                  <AnimatePresence initial={false} mode="wait">
                    {activeSectionType && section.type === activeSectionType
                      ? renderSearchBarSectionDropdown(section)
                      : null}
                  </AnimatePresence>
                </div>
              ))}

              <div className={classNames(styles.button)} />
            </div>

            <div className={classNames(styles.underBarWrapper)}>
              <AnimatePresence initial={false} mode="wait">
                {activeLocationSection && (
                  <SearchBarLocationsDropdown
                    key={`locations-${activeLocationSection.id}`}
                    className={classNames(styles.isLocationsDropdown)}
                    description={
                      Array.isArray(activeLocationSection.value) &&
                      activeLocationSection.value.length > 0
                        ? activeLocationSection.value
                        : activeLocationSection.placeholder
                    }
                    isLoading={isLoadingLocations}
                    isVisible
                    label={activeLocationSection.label}
                    locations={
                      (activeLocationSection.dropdownData as LocationData)
                        ?.locations ?? []
                    }
                    onChangeLocations={onChangeLocations}
                    onChangeTransportation={onChangeTransportation}
                    onClickBack={onClickBack}
                    onClickClose={onClickClose}
                    onClickNext={onClickNext}
                    periodType={
                      (
                        durationSection?.dropdownData as DurationData
                      ).durationOptions.find(option => option.isSelected)
                        ?.id as Content.Components['PeriodType']
                    }
                    searchSettings={searchSettings}
                    transportationOptions={
                      (activeLocationSection.dropdownData as LocationData)
                        ?.transportationOptions ?? []
                    }
                  />
                )}

                {activeDateSection && (
                  <SearchBarDateDropdown
                    key={`date-${activeDateSection.id}`}
                    availableDates={
                      (activeDateSection.dropdownData as DateData)
                        ?.availableDates ?? []
                    }
                    className={classNames(styles.isDateDropdown)}
                    customRangeStyle={customRangeStyle}
                    description={
                      activeDateSection.value && activeDateSection.value !== ''
                        ? activeDateSection.value
                        : activeDateSection.placeholder
                    }
                    focusFirstSelectableMonth={
                      (activeDateSection.dropdownData as DateData)
                        ?.focusFirstSelectableMonth
                    }
                    isLoading={isLoadingDate}
                    isVisible
                    label={activeDateSection.label}
                    maxDate={
                      (activeDateSection.dropdownData as DateData)
                        ?.maxDate as Date
                    }
                    maxRangeDays={
                      (activeDateSection.dropdownData as DateData)?.maxRangeDays
                    }
                    minDate={
                      (activeDateSection.dropdownData as DateData)
                        ?.minDate as Date
                    }
                    onChange={onChangeDate}
                    onClickBack={onClickBack}
                    onClickClose={onClickClose}
                    onClickNext={onClickNext}
                    onClickSearch={onClickSearch}
                    rangeSelect={
                      (activeDateSection.dropdownData as DateData)?.rangeSelect
                    }
                    rangeSize={
                      (activeDateSection.dropdownData as DateData)?.rangeSize
                    }
                    selectedDate={
                      (activeDateSection.dropdownData as DateData)?.selectedDate
                    }
                  />
                )}
              </AnimatePresence>
            </div>
          </React.Fragment>
        ) : (
          <SearchBarWidgetDropdown
            customRangeStyle={customRangeStyle}
            onChangeDate={onChangeDate}
            onChangeDuration={onChangeDuration}
            onChangeLocations={onChangeLocations}
            onChangePersons={onChangePersons}
            onChangeTransportation={onChangeTransportation}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            section={widgetSection}
          />
        )}
      </div>
    );
  },
);

SearchBarDropdown.displayName = 'SearchBarDropdown';

export default SearchBarDropdown;
